import { useApi } from '@hero/context';
import type {
  UseMemberApi,
  UseMemberListApi,
  UseMemberCreateApi,
  UseMemberUpdateApi,
  UseCurrentMemberApi,
  UseCurrentMemberUpdateApi,
  UseMemberDriversApi,
} from './types';
import { useInfiniteQueryAPI, useMutationAPI, useQueryAPI } from '../../utils';

/**
 * Get a single member by ID.
 *
 * Hook API: {@link UseMemberApi}
 */
export const useMemberApi: UseMemberApi = (id, apiConfig) => {
  const api = useApi();
  return useQueryAPI({
    entity: 'member',
    apiFn: api.members.getOneMember,
    query: undefined,
    singleIdQuery: id,
    apiConfig,
    isEnabled: () => !!id,
  });
};

/**
 * List members with pagination (skip/limit).
 * API from ApiProvider (useApi()); works in any app without overrides.
 *
 * Hook API: {@link UseMemberListApi}
 */
export const useMemberListApi: UseMemberListApi = (query, apiConfig) => {
  const api = useApi();
  return useInfiniteQueryAPI({
    entity: 'member',
    apiFn: api.members.getUsers,
    query,
    apiConfig,
    isEnabled: () => true,
  });
};

/**
 * Create member. Handles cache invalidation and optional onSuccess/onError.
 *
 * Hook API: {@link UseMemberCreateApi}
 */
export const useMemberCreateApi: UseMemberCreateApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'member',
    apiFn: api.members.createMember,
    apiConfig,
  });
};

/**
 * Update member. Handles cache invalidation and optional onSuccess/onError.
 *
 * Hook API: {@link UseMemberUpdateApi}
 */
export const useMemberUpdateApi: UseMemberUpdateApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'member',
    apiFn: api.members.updateMember,
    apiConfig,
  });
};

/**
 * Get the current authenticated member.
 *
 * Hook API: {@link UseCurrentMemberApi}
 */
export const useCurrentMemberApi: UseCurrentMemberApi = apiConfig => {
  const api = useApi();
  return useQueryAPI({
    entity: 'member',
    apiFn: api.members.getCurrentMember,
    query: undefined,
    apiConfig,
  });
};

/**
 * Update current member (self). Handles cache invalidation.
 *
 * Hook API: {@link UseCurrentMemberUpdateApi}
 */
export const useCurrentMemberUpdateApi: UseCurrentMemberUpdateApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'member',
    apiFn: api.members.updateCurrentMember,
    apiConfig,
  });
};

/**
 * List drivers (members with driver type). Carrier-specific.
 * API from ApiProvider (useApi('carrier')); pass apiConfig to override apiFn, limit, etc.
 *
 * Hook API: {@link UseMemberDriversApi}
 */
export const useMemberDriversApi: UseMemberDriversApi = (query, apiConfig) => {
  const api = useApi('carrier');
  return useInfiniteQueryAPI({
    entity: 'driverMember',
    apiFn: api.members.getDrivers,
    query,
    apiConfig,
    isEnabled: () => true,
  });
};
